/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, MenuItem, TextField, Radio } from '@mui/material';
import { Card, CardHeader, CardContent, Typography, Button, Table, TableBody, TableHead, TableCell, TableContainer, TableRow,
  Switch, Tooltip, Chip } from '@mui/material';
import {  ConfigurationModel,  Int32StringKeyValuePair, DeliveryPricingRuleResponse, DateOnly } from '../../../../app/services/api/ofdClient';
import { OnboardingStatusEnum } from '../enums/OnboardingStatusEnum';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';

const formatDateOnly = (d?: DateOnly): string =>
  d ? `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}` : '';

const formatRuleDate = (d?: DateOnly | string): string => {
  if (!d) return '';

  if (typeof d === 'string') {
    const match = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
  }

  return formatDateOnly(d);
};

interface DeliveryMethodsSectionProps {
  methods?: any[];
  handleChange: Function;
}

const DeliveryMethodsSection: React.FC<DeliveryMethodsSectionProps> = ({
  methods = [],
  handleChange,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [defaultMethodId, setDefaultMethodId] = useState<number | null>(null);
  const enabledCount = rows.filter(r => r.enabled).length;
  const { t } = useTranslation('onboardingWizard');


  useEffect(() => {
    let initialRows = methods.map(method => ({
      ...method,
      charge: method.charge ? parseInt(method.charge.toString()) : 0,
      isDefault: method.isDefault || false,
    }));
  
    if (initialRows.length > 0 && !initialRows.some(r => r.enabled)) {
      initialRows = initialRows.map((r, i) => (i === 0 ? { ...r, enabled: true } : r));
    }
    // Set default method (first enabled method or first method if none enabled)
    const defaultMethod = initialRows.find(r => r.isDefault) || 
                       initialRows.find(r => r.enabled) || 
                       initialRows[0];
    if (defaultMethod) {
      const defaultId = defaultMethod.shippingMethodId || defaultMethod.id;
      setDefaultMethodId(defaultMethod.shippingMethodId || defaultMethod.id);
      initialRows = initialRows.map(row => ({
        ...row,
        isDefault: row.shippingMethodId === defaultId,
      }));
    }
    setRows(initialRows);
  }, [methods]);

  const handleToggle = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const on = e.target.checked;
  
    if (!on && defaultMethodId === rows[idx].shippingMethodId) {
      return; 
    }
  
    setRows(prev => {
      const updated = [...prev];
      const currentMethod = updated[idx];
      let newDefaultId = defaultMethodId;
  
      if (!on) {
        updated[idx] = { ...updated[idx], enabled: false, charge: 0 };
        if (!updated.some(r => r.enabled)) {
          updated[idx] = { ...updated[idx], enabled: true, charge: prev[idx].charge };
          newDefaultId = currentMethod.shippingMethodId;
          setDefaultMethodId(newDefaultId);
        }
      } else {
        updated[idx] = { ...updated[idx], enabled: true };
        if (!defaultMethodId) {
          newDefaultId = currentMethod.shippingMethodId;
          setDefaultMethodId(newDefaultId);
        }
      }
  
      const finalUpdated = updated.map(row => ({
        ...row,
        isDefault: row.shippingMethodId === newDefaultId,
      }));
  
      handleChange({ target: { value: finalUpdated } }, 'retailerDeliveryMethods');
      return finalUpdated;
    });
  };

  const handleDefaultChange = (methodId: number) => {
    setDefaultMethodId(methodId);
    setRows(prev => {
      const updated = prev.map(row => ({
        ...row,
        isDefault: row.shippingMethodId === methodId, 
      }));

      handleChange({ target: { value: updated } }, 'retailerDeliveryMethods');
      return updated;
    });
  };

  const handleChargeChange = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || /^\d+$/.test(val)) {
      setRows(prev => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], charge: val };
      
        const currentDefaultId = defaultMethodId;
        const finalUpdated = updated.map(row => ({
          ...row,
          isDefault: row.shippingMethodId === currentDefaultId,
        }));
      
        handleChange({ target: { value: finalUpdated } }, 'retailerDeliveryMethods');
        return finalUpdated;
      });
    }
  };

  const formatCharge = (idx: number, value: string) => {
    const numValue = parseInt(value) || 0;
    setRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], charge: numValue };
  
      const currentDefaultId = defaultMethodId;
      const finalUpdated = updated.map(row => ({
        ...row,
        isDefault: row.shippingMethodId === currentDefaultId,
      }));
  
      handleChange({ target: { value: finalUpdated } }, 'retailerDeliveryMethods');
      return finalUpdated;
    });
  };

  const setAll = (enabled: boolean) => {
    setRows(prev => {
      let updated;
      let newDefaultId = defaultMethodId;
    
      if (!enabled) {
        updated = prev.map((r, i) => (i === 0 ? { ...r, enabled: true } : { ...r, enabled: false, charge: 0 }));
        newDefaultId = updated[0].shippingMethodId;
        setDefaultMethodId(newDefaultId);
      } else {
        updated = prev.map(r => ({ ...r, enabled: true }));
        if (!defaultMethodId && updated.length > 0) {
          newDefaultId = updated[0].shippingMethodId;
          setDefaultMethodId(newDefaultId);
        }
      }
    
      // Use newDefaultId instead of defaultMethodId
      const finalUpdated = updated.map(row => ({
        ...row,
        isDefault: row.shippingMethodId === newDefaultId,
      }));
    
      handleChange({ target: { value: finalUpdated } }, 'retailerDeliveryMethods');
      return finalUpdated;
    });
  };

  return (
    <Card sx={{ mt: 6, width: '100%' }}>
      <CardHeader
        title={
          <Typography variant="textLG">
            {t('configurationTab.deliverymethodandcharge')} 
          </Typography>
        }
  
        subheader={
          <Box
            display="flex"
            alignItems="center"
            gap={2}>
            <Typography variant="textSM"
              color="text.secondary">
              {t('configurationTab.active')} {enabledCount}/{rows.length}
            </Typography>
            <Typography
              variant="textSM"
              onClick={() => setAll(true)}
              sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline',
                color: 'primary.dark',
              }, '&:focus': {
                outline: 'none',
                textDecoration: 'underline',
              },
              }}
            >
              {t('configurationTab.enableAll')}
            </Typography>

            <Typography
              variant="textSM"
              onClick={() => setAll(false)}
              sx={{ cursor: 'pointer', color: 'primary.main',  textDecoration: 'none', '&:hover': { textDecoration: 'underline',
                color: 'primary.dark',
              }, '&:focus': {
                outline: 'none',
                textDecoration: 'underline',
              },
              }}
            >
              {t('configurationTab.disableAll')}
            </Typography>
          </Box>
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.deliveryMethod')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.enable')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.default')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.deliveryCharge')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
         
              {rows.map((row, idx) => (
                <TableRow key={row.shippingMethodId ?? row.id}>
                  <TableCell sx={{ width: '40%' }}>
                    <Box display="flex"
                      alignItems="center"
                      gap={1}>
                      <Typography variant="textSM">{row.name}</Typography>
                      {row.info && (
                        <Tooltip title={row.info}>
                          <Typography variant="textSM"
                            color="text.secondary"
                            sx={{ cursor: 'help' }}>
                            ⓘ
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: '20%' }}>
                    <Switch checked={row.enabled}
                      onChange={handleToggle(idx)}
                      color="primary"
                      size="small" />
                  </TableCell>
                  <TableCell sx={{ width: '20%' }}>
                    <Radio
                      checked={defaultMethodId === row.shippingMethodId}
                      onChange={() => handleDefaultChange(row.shippingMethodId)}
                      value={row.shippingMethodId}
                      disabled={!row.enabled}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ width: '40%' }}>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{ 
                        min: 0,
                        inputMode: 'numeric', 
                      }}
                      value={row.enabled ? (row.charge?.toString() || '0') : '0'}
                      onChange={handleChargeChange(idx)}
                      disabled={!row.enabled}
                      sx={{ width: 110 }}
                      onBlur={e => {
                        if (row.enabled) {
                          formatCharge(idx, e.target.value || '0');
                        }
                      }}
                      onKeyDown={e => {
                        if (['.', '-', '+', 'e', 'E'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

interface RetailerMethodVm {
  shippingMethodId: number;
  enabled: boolean;
  charge?: number | null;
  isDefault: boolean;
}

interface OnboardingCofigurationProps {
  formData: ConfigurationModel;
  handleChange: Function;
  applicationStatus: number;
  palletSizes: Int32StringKeyValuePair[];
  containerSizes: Int32StringKeyValuePair[];
  catalogues: Int32StringKeyValuePair[];
  orderCategories: Int32StringKeyValuePair[];
  franchiseIsSelected: boolean;
  retailerMethods?: RetailerMethodVm[];
  showDeliveryMethods?: boolean;
  retailerId?: number;
}

const OnboardingConfiguration: React.FC<OnboardingCofigurationProps> = ({
  formData,
  handleChange,
  applicationStatus,
  palletSizes,
  containerSizes,
  catalogues,
  franchiseIsSelected,
  retailerMethods,
  showDeliveryMethods = false,
  retailerId,
}) => {
  const { t } = useTranslation('onboardingWizard');
  const { hasPermissionTo } = useContext(UserContext);
  const { deliveryPricingClient } = useNswagClient();
  const deliveryPricingClientRef = useRef(deliveryPricingClient);

  useEffect(() => {
    deliveryPricingClientRef.current = deliveryPricingClient;
  }, [deliveryPricingClient]);
  const textFieldMargin = { mb: 8 };
  const isDraft = applicationStatus == OnboardingStatusEnum.DRAFT;
  const deliveryMethodsToShow = retailerMethods ?? [];
  const [pricingRules, setPricingRules] = useState<DeliveryPricingRuleResponse[]>([]);
  const showPricing = hasPermissionTo(['DeliveryPricingView']) && showDeliveryMethods;

  useEffect(() => {
    if (showPricing && retailerId) {
      deliveryPricingClientRef.current.rulesGET(retailerId).then(res => setPricingRules(res.items ?? [])).catch(() => setPricingRules([]));
    } else {
      setPricingRules([]);
    }
  }, [retailerId, showPricing]);
  
  return (
    
    <Box sx={{ p: 6, paddingBottom: 0 }}>
      <Grid container
        spacing={3}>
        <Grid item
          xs={12}
          md={6}
          sx={{ px: { xs: 0, md: 4 } }}>
          <TextField
            required
            fullWidth
            label={t('configurationTab.storeId')}
            value={formData.storeId ?? ''}
            onChange={e => handleChange(e, 'storeId')}
            sx={textFieldMargin}
            inputProps={{ maxLength: 50 }}
            disabled={!isDraft}
          />

          <TextField
            required
            fullWidth
            label={t('configurationTab.externalIdentifier')}
            value={formData.externalIdentifier ?? ''}
            onChange={e => handleChange(e, 'externalIdentifier')}
            sx={textFieldMargin}
          />

          <TextField
            select
            required
            fullWidth
            label={t('configurationTab.containerSize')}
            value={formData.containerSizeId ?? ''}
            onChange={e => handleChange(e, 'containerSizeId')}
            sx={textFieldMargin}
          >
            {containerSizes.map(option => (
              <MenuItem key={option.key}
                value={option.key}>
                {option.value}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            required
            type="number"
            label={t('configurationTab.minContainerFillPercentage')}
            value={formData.minContainerFillPercentage ?? ''}
            onChange={e => handleChange(e, 'minContainerFillPercentage')}
            sx={textFieldMargin}
            onKeyDown={e => {
              if (['e', 'E', '+', '-'].indexOf(e.key) !== -1) e.preventDefault();
            }}
            inputProps={{ min: 0, max: 100, step: 0.01, inputMode: 'decimal' }}
            error={
              (formData.minContainerFillPercentage ?? null) !== null &&
              (Number(formData.minContainerFillPercentage) < 0 ||
                Number(formData.minContainerFillPercentage) > 100)
            }
            helperText={
              (formData.minContainerFillPercentage ?? null) !== null &&
              (Number(formData.minContainerFillPercentage) < 0 ||
                Number(formData.minContainerFillPercentage) > 100)
                ? t('validation.percentageInvalid')
                : ''
            }
          />
          <TextField
            fullWidth
            required
            type="number"
            label={t('configurationTab.claimPeriod')}
            value={formData.claimPeriod ?? 5}
            onChange={e => handleChange(e, 'claimPeriod')}
            onKeyDown={e => {
              if (['e', 'E', '+', '-'].indexOf(e.key) !== -1) e.preventDefault();
            }}
            inputProps={{ min: 0, step: 1, inputMode: 'decimal' }}
            sx={textFieldMargin}
          />
        </Grid>

        <Grid item
          xs={12}
          md={6}
          sx={{ px: { xs: 0, md: 4 } }}>
          <TextField
            select
            fullWidth
            type="number"
            required
            label={t('configurationTab.palletSize')}
            value={formData.palletSizeId ?? ''}
            onChange={e => handleChange(e, 'palletSizeId')}
            sx={textFieldMargin}
            inputProps={{ min: 0 }}
          >
            {palletSizes.map(option => (
              <MenuItem key={option.key}
                value={option.key}>
                {option.value}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            required
            disabled={!franchiseIsSelected}
            label={t('configurationTab.catalogue')}
            value={formData.catalogues || []}
            onChange={e => handleChange(e, 'catalogues')}
            sx={textFieldMargin}
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) =>
                catalogues
                  .filter(option => selected.includes(option.key))
                  .map(option => option.value)
                  .join(', '),
            }}
          >
            {catalogues?.map(option => (
              <MenuItem key={option.key}
                value={option.key}>
                {option.value}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            type="number"
            label={t('configurationTab.creditLimit')}
            value={formData.creditLimit ?? ''}
            onChange={e => handleChange(e, 'creditLimit')}
            onKeyDown={e => {
              if (['e', 'E', '+', '-'].indexOf(e.key) !== -1) e.preventDefault();
            }}
            inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
            sx={textFieldMargin}
          />

          <TextField
            fullWidth
            type="number"
            label={t('configurationTab.leadTime')}
            value={formData.leadTime ?? ''}
            onChange={(e) => handleChange(e, 'leadTime')}
            onKeyDown={(e) => {
              const invalidKeys = ['e', 'E', '+', '-', '.'];
              if (invalidKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
            sx={textFieldMargin}
            inputProps={{ 
              min: 0,
              max: 365,
              step: 1,
              inputMode: 'numeric',
            }}
            error={
              formData.leadTime !== undefined && formData.leadTime !== null &&
              (Number(formData.leadTime) < 0 || Number(formData.leadTime) > 365)
            }
            helperText={
              formData.leadTime !== undefined && formData.leadTime !== null &&
              (Number(formData.leadTime) < 0 || Number(formData.leadTime) > 365)
                ? t('validation.leadTimeInvalid')
                : t('configurationTab.leadTimeHelpText')
            }
          />
        </Grid>
      </Grid>

      {showDeliveryMethods && (
        <DeliveryMethodsSection methods={deliveryMethodsToShow}
          handleChange={handleChange} />
      )}

      {showPricing && pricingRules.length > 0 && (
        <Card sx={{ mt: 4, width: '100%' }}>
          <CardHeader
            title={
              <Typography variant="textLG">
                {t('configurationTab.deliveryPricingRules', 'Delivery Pricing Rules')}
              </Typography>
            }
            sx={{ pb: 0 }}
          />
          <CardContent sx={{ pt: 1 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.deliveryPricingModel', 'Model')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.deliveryPricingScope', 'Scope')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.deliveryPricingEffective', 'Effective')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('configurationTab.deliveryPricingStatus', 'Status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pricingRules.map(rule => (
                    <TableRow key={rule.deliveryPricingRuleId}>
                      <TableCell>{rule.pricingModelName}</TableCell>
                      <TableCell>
                        {rule.retailerIds && rule.retailerIds.length > 0
                          ? t('configurationTab.deliveryPricingScopeRetailer', 'Retailer Override')
                          : t('configurationTab.deliveryPricingScopeGlobal', 'Global Default')}
                      </TableCell>
                      <TableCell>
                        {formatRuleDate(rule.effectiveFrom as DateOnly | string)}{rule.effectiveTo ? ` — ${formatRuleDate(rule.effectiveTo as DateOnly | string)}` : '+'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rule.enabled
                            ? t('configurationTab.deliveryPricingStatusActive', 'Active')
                            : t('configurationTab.deliveryPricingStatusDisabled', 'Disabled')}
                          color={rule.enabled ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OnboardingConfiguration;
